/**
 * LmsMyLearning — user-facing enrolled courses.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { BookOpen, Clock, CheckCircle2, PlayCircle, Award } from "lucide-react";

export default function LmsMyLearning() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/me/enrollments`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="My Learning" description="Courses assigned to you" />
      {rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center">
          <BookOpen className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No enrolled courses yet.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((e) => (
          <div key={e.id} className="border border-border p-4 hover:border-primary cursor-pointer" onClick={() => navigate(`/app/lms/learn/${e.course_id}`)} data-testid={`lms-my-course-${e.course_id}`}>
            <div className="flex items-start gap-2">
              <BookOpen className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{e.course_title}</div>
                <div className="text-[10px] uppercase text-muted-foreground mt-0.5 flex gap-2">
                  <span className={`chip text-[9px] ${e.mandatory ? "chip-warning" : "chip-info"}`}>{e.mandatory ? "MANDATORY" : "OPTIONAL"}</span>
                  <span className="chip text-[9px]">{e.status.replace("_", " ")}</span>
                </div>
              </div>
              {e.status === "completed" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
            </div>
            {e.course_description && <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{e.course_description}</p>}
            <div className="mt-3">
              <div className="w-full h-2 bg-muted rounded-none">
                <div className="h-2 bg-emerald-500" style={{ width: `${e.progress_percent || 0}%` }} />
              </div>
              <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                <Clock className="w-3 h-3" />{e.course_duration_min || 0}m
                <span className="ml-auto">{e.progress_percent || 0}%</span>
                {e.final_score != null && <Award className="w-3 h-3 text-amber-500 ml-1" title={`Score ${e.final_score}%`} />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
